import { InputCapture } from '@canvas/input';

import { EffectManager } from './effects/manager/effect-manager';
import { type SelectionEffectFactory } from './effects/types';
import { createSharedEventLoop, type SharedEventLoop } from './inputs/event-loop';
import { InputManager } from './inputs/manager/input-manager';
import { type SelectionInputFactory } from './inputs/types';
import {
  SelectionsMachine,
  type SelectionsMachineConstructorOptions,
} from './machine/selection-machine';

/**
 * Infrastructure objects created by `setup()` and passed to the
 * feature builder callback so modes can construct core input features
 * (mouse, keyboard) that depend on them.
 */
export interface SetupContext {
  inputCapture: InputCapture;
  sharedLoop: SharedEventLoop;
}

export type FeatureBuilder = (ctx: SetupContext) => {
  inputs: SelectionInputFactory[];
  effects: SelectionEffectFactory[];
};

/**
 * Initializes the selections machine for all selections state, effects, etc.
 * in Databases.
 *
 * Usage:
 * ```
 * const { machine, setup } = initializeSelectionsMachine({
 *   reducerPolicies: { canRequestCellEdit: () => true, canClearCells: () => true },
 * })
 *
 * const destroy = setup((ctx) => {
 *   const features = modeConfig.buildSelectionFeatures({ ...services, ...ctx });
 *
 *   return resolveFeatures(features);
 * });
 *
 * ..
 *
 * // on cleanup
 * destroy();
 * ```
 */
export function initializeSelectionsMachine(
  machineOptions: SelectionsMachineConstructorOptions
) {
  const machine = new SelectionsMachine(machineOptions);

  /**
   * Because items like input capture and the corresponding event
   * loop are created inside the selections machine, yet features / inputs
   * are registered OUTSIDE of this, the context is passed into this method.
   */
  function setup(buildFeatures: FeatureBuilder) {
    const inputCapture = InputCapture.from(document.body, undefined, {
      disableIgnoredEvents: true,
      disableCanvasScroll: true,
    });
    const sharedLoop = createSharedEventLoop(inputCapture);
    const { inputs, effects } = buildFeatures({ inputCapture, sharedLoop });

    const inputManager = new InputManager(machine, inputs);
    const effectManager = new EffectManager(machine, effects);

    sharedLoop.start();
    inputManager.setup();
    effectManager.setup();

    return function destroy() {
      effectManager.dispose();
      inputManager.dispose();
      inputCapture.dispose();
      sharedLoop.stop();
    };
  }

  return {
    machine,
    setup,
  };
}
